/* eslint-disable jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */
import * as React from "react";
import PropTypes from "prop-types";
import { Link, NavLink, useRouteMatch, useLocation } from "react-router-dom";
import clsx from "clsx";

import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import MenuIcon from "@material-ui/icons/Menu";
import Avatar from "@material-ui/core/Avatar";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";

import logo from "common/assets/logo.svg";
import appVersion from "common/config/app-version.config";
import sectionType from "common/proptypes/section.proptypes";
import reactChildrenType from "common/proptypes/react-children.proptypes";
import { SECTION_BUDGET } from "common/routes/sections/main.sections";
import sidebarStyle from "./sidebar.styles";

const useStyles = makeStyles(sidebarStyle);

function NavItem({ onClick: handleClick, menuIcon, menuText, isActive }) {
  const classes = useStyles();
  return (
    <ListItem
      button
      onClick={handleClick}
      className={clsx(isActive && classes.active)}
    >
      <ListItemIcon>{menuIcon}</ListItemIcon>
      <Typography
        className={classes.menuText}
        variant="overline"
        display="block"
        gutterBottom
      >
        {menuText}
      </Typography>
    </ListItem>
  );
}
NavItem.propTypes = {
  onClick: PropTypes.func,
  menuIcon: reactChildrenType.isRequired,
  menuText: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
};
NavItem.defaultProps = {
  onClick: null,
  isActive: false,
};

function Sidebar({
  className,
  toggleDrawer,
  isPhone,
  drawerOpen,
  setDrawerOpen,
  sections,
  handleStartClick,
  imageUrl,
  handleClick,
  initials,
  anchorEl,
  handleClose,
  handleLogout,
}) {
  const match = useRouteMatch();
  const location = useLocation();
  const [menuAnchorElement, setMenuAnchorElement] = React.useState(null);

  const handleOpenMenu = (event) => {
    setMenuAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorElement(null);
  };

  const classes = useStyles();

  return (
    <aside className={clsx(classes.sidebar, className)}>
      <IconButton
        edge="start"
        aria-label="Open menu"
        className={classes.menu}
        onClick={toggleDrawer}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        variant={isPhone ? "temporary" : "permanent"}
        anchor={isPhone ? "bottom" : "left"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{
          paper: classes.drawerPaper,
          paperAnchorDockedLeft: classes.drawerAnchorLeft,
        }}
      >
        <Hidden xsDown>
          <Link to={match.url} className={classes.logoContainer}>
            <img src={logo} title="FYIsoft BudgetFYI" alt="Home" />
          </Link>
        </Hidden>
        <div className={classes.nav}>
          <List component="nav" aria-label="overview notifications users">
            {sections.map((section) =>
              section.menuItems ? (
                <React.Fragment key={section.id}>
                  <NavItem
                    onClick={handleOpenMenu}
                    menuIcon={section.menuIcon}
                    menuText={section.menuText}
                    isActive={location.pathname.includes(section.path)}
                  />
                  <Menu
                    anchorEl={menuAnchorElement}
                    keepMounted
                    open={Boolean(menuAnchorElement)}
                    onClose={handleCloseMenu}
                  >
                    {section.menuItems.map((menuItem) => (
                      <NavLink
                        key={menuItem.path}
                        to={`${match.url}${section.path}${menuItem.path}`}
                        className={classes.link}
                        activeClassName={classes.active}
                        exact={section.path === ""}
                      >
                        <MenuItem onClick={handleCloseMenu}>
                          {menuItem.text}
                        </MenuItem>
                      </NavLink>
                    ))}
                  </Menu>
                </React.Fragment>
              ) : (
                <NavLink
                  key={section.id}
                  to={`${match.url}${section.path}`}
                  className={classes.link}
                  activeClassName={classes.active}
                  exact={section.path === ""}
                >
                  <NavItem
                    onClick={
                      section.path === SECTION_BUDGET.path
                        ? handleStartClick
                        : null
                    }
                    menuIcon={section.menuIcon}
                    menuText={section.menuText}
                  />
                </NavLink>
              ),
            )}
          </List>
        </div>
        <div className={classes.profile}>
          <div
            aria-controls="user-menu"
            aria-haspopup="true"
            onClick={handleClick}
          >
            {imageUrl ? (
              <Avatar
                className={classes.avatar}
                sizes="40"
                alt={initials}
                src={imageUrl}
              />
            ) : (
              <Avatar className={classes.avatar}>{initials}</Avatar>
            )}
          </div>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem>FYIsoft BudgetFYI - {appVersion}</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Sign out</MenuItem>
          </Menu>
        </div>
      </Drawer>
    </aside>
  );
}

Sidebar.propTypes = {
  className: PropTypes.string,
  toggleDrawer: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(sectionType).isRequired,
  handleStartClick: PropTypes.func.isRequired,
  imageUrl: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  initials: PropTypes.string.isRequired,
  anchorEl: PropTypes.instanceOf(Object),
  handleClose: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
};

Sidebar.defaultProps = {
  className: "",
  imageUrl: null,
  anchorEl: null,
};

export default Sidebar;
